import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MessageCircle,
  Smartphone,
  Phone,
  ShoppingCart,
  CreditCard,
  RotateCcw,
  StickyNote,
  Plus,
  Search,
  Filter,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useCustomerCommunications,
  useAddCustomerNote,
} from "@/hooks/admin/useCRM";
import type { CommunicationType, CommunicationEntry } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Communication type config
// ---------------------------------------------------------------------------

interface TypeConfig {
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly dotColor: string;
  readonly bgColor: string;
  readonly textColor: string;
}

const TYPE_CONFIG: Record<CommunicationType, TypeConfig> = {
  order: {
    label: "Commande",
    icon: <ShoppingCart className="h-4 w-4" />,
    dotColor: "bg-amber-400",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
  },
  payment: {
    label: "Paiement",
    icon: <CreditCard className="h-4 w-4" />,
    dotColor: "bg-emerald-400",
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
  },
  whatsapp: {
    label: "WhatsApp",
    icon: <MessageCircle className="h-4 w-4" />,
    dotColor: "bg-green-400",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
  },
  sms: {
    label: "SMS",
    icon: <Smartphone className="h-4 w-4" />,
    dotColor: "bg-blue-400",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
  },
  call: {
    label: "Appel",
    icon: <Phone className="h-4 w-4" />,
    dotColor: "bg-violet-400",
    bgColor: "bg-violet-50",
    textColor: "text-violet-700",
  },
  return: {
    label: "Retour",
    icon: <RotateCcw className="h-4 w-4" />,
    dotColor: "bg-orange-400",
    bgColor: "bg-orange-50",
    textColor: "text-orange-700",
  },
  admin_note: {
    label: "Note admin",
    icon: <StickyNote className="h-4 w-4" />,
    dotColor: "bg-stone-400",
    bgColor: "bg-stone-50",
    textColor: "text-stone-600",
  },
};

const ALL_TYPES: CommunicationType[] = [
  "order",
  "payment",
  "whatsapp",
  "sms",
  "call",
  "return",
  "admin_note",
];

// ---------------------------------------------------------------------------
// Add note modal
// ---------------------------------------------------------------------------

function AddNoteModal({
  customerId,
  open,
  onClose,
}: {
  customerId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const addNote = useAddCustomerNote(customerId);

  const handleSubmit = () => {
    if (!text.trim()) {
      toast.error("La note ne peut pas être vide.");
      return;
    }
    addNote.mutate(
      { text },
      {
        onSuccess: () => {
          toast.success("Note ajoutée.");
          setText("");
          onClose();
        },
        onError: () => toast.error("Erreur lors de l'ajout de la note."),
      },
    );
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Ajouter une note"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={addNote.isPending}>
            {addNote.isPending ? "Enregistrement..." : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-500">
          Cette note sera visible dans le journal de communications du client.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Votre note..."
          autoFocus
          className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
        />
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Timeline entry
// ---------------------------------------------------------------------------

function TimelineEntry({ entry }: { entry: CommunicationEntry }) {
  const cfg = TYPE_CONFIG[entry.type];
  const date = new Date(entry.created_at);

  return (
    <div className="flex gap-4">
      {/* Dot + line */}
      <div className="flex flex-col items-center">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cfg.bgColor} ${cfg.textColor}`}>
          {cfg.icon}
        </div>
        <div className="mt-1 w-px flex-1 bg-stone-100" />
      </div>

      {/* Content */}
      <div className="pb-5 flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bgColor} ${cfg.textColor}`}>
              {cfg.label}
            </span>
            <p className="text-sm font-medium text-stone-900">{entry.title}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {entry.admin_user_name && (
              <span className="text-xs text-stone-400">{entry.admin_user_name}</span>
            )}
            <time className="text-xs text-stone-400 tabular-nums">
              {date.toLocaleDateString("fr-SN")}{" "}
              {date.toLocaleTimeString("fr-SN", { hour: "2-digit", minute: "2-digit" })}
            </time>
          </div>
        </div>
        {entry.body && (
          <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{entry.body}</p>
        )}
        {entry.reference_id && (
          <p className="mt-1 text-xs text-stone-400">Réf : {entry.reference_id}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer search (shown when no customer_id in URL)
// ---------------------------------------------------------------------------

function CustomerSearch({ onSelect }: { onSelect: (id: string, name: string) => void }) {
  const [query, setQuery] = useState("");

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 text-stone-400">
        <Search className="h-6 w-6" />
      </div>
      <div>
        <h2 className="font-display text-lg font-semibold text-stone-900">
          Sélectionner un client
        </h2>
        <p className="mt-1 text-sm text-stone-500 max-w-sm">
          Saisissez l'identifiant du client pour afficher son journal de communications.
        </p>
      </div>
      <div className="flex w-full max-w-sm gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ID client..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && query.trim()) onSelect(query.trim(), query.trim());
          }}
          className="flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
        />
        <Button
          onClick={() => { if (query.trim()) onSelect(query.trim(), query.trim()); }}
          disabled={!query.trim()}
        >
          Afficher
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Communications() {
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get("customer_id");
  const urlCustomerName = searchParams.get("customer_name");

  const [customerId, setCustomerId] = useState<string | null>(urlCustomerId);
  const [customerName, setCustomerName] = useState<string | null>(urlCustomerName);
  const [typeFilter, setTypeFilter] = useState<CommunicationType | null>(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const { data, isLoading } = useCustomerCommunications(customerId);

  const handleSelectCustomer = useCallback((id: string, name: string) => {
    setCustomerId(id);
    setCustomerName(name);
  }, []);

  const resolvedName = data?.customer_name ?? customerName ?? customerId ?? "Client";

  const allEntries: readonly CommunicationEntry[] = data?.communications ?? [];
  const filteredEntries = typeFilter
    ? allEntries.filter((e) => e.type === typeFilter)
    : allEntries;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-stone-100 text-stone-600">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Journal de communications
              </h1>
              {customerId && (
                <p className="text-sm text-stone-500">{resolvedName}</p>
              )}
            </div>
          </div>
          {customerId && (
            <Button
              onClick={() => setNoteModalOpen(true)}
              className="gap-2 shrink-0"
            >
              <Plus className="h-4 w-4" />
              Ajouter une note
            </Button>
          )}
        </div>

        {/* No customer selected */}
        {!customerId && (
          <CustomerSearch onSelect={handleSelectCustomer} />
        )}

        {/* Customer selected */}
        {customerId && (
          <>
            {/* Type filter */}
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-stone-400 shrink-0" />
              <button
                onClick={() => setTypeFilter(null)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  typeFilter === null
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                Tous
              </button>
              {ALL_TYPES.map((type) => {
                const cfg = TYPE_CONFIG[type];
                return (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                    className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      typeFilter === type
                        ? `${cfg.bgColor} ${cfg.textColor} ring-1`
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {cfg.icon}
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Timeline */}
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              {isLoading ? (
                <div className="space-y-6 animate-pulse">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="h-8 w-8 rounded-full bg-stone-200 shrink-0" />
                      <div className="flex-1 space-y-2 pt-1">
                        <div className="h-4 w-48 rounded bg-stone-100" />
                        <div className="h-3 w-72 rounded bg-stone-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredEntries.length === 0 ? (
                <EmptyState
                  icon={<MessageCircle className="h-6 w-6" />}
                  title="Aucune communication"
                  description={
                    typeFilter
                      ? "Aucune communication de ce type pour ce client."
                      : "Ce client n'a pas encore d'historique de communication."
                  }
                />
              ) : (
                <div>
                  {filteredEntries.map((entry) => (
                    <TimelineEntry key={entry.id} entry={entry} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Add note modal */}
        {customerId && (
          <AddNoteModal
            customerId={customerId}
            open={noteModalOpen}
            onClose={() => setNoteModalOpen(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
}
